import { useEffect, useState } from "react";
import styles from "./lesson.module.scss";
import LessonNavigationBar from "@/components/lessonNavigation/LessonNavigationBar";
import QuestionForm from "@/components/sections/questionForm/QuestionForm";
import Media from "@/components/sections/media/Media";
import Book from "@/components/sections/book/Book";
import { CurrentSection, LessonData, Section, SectionsList, setLessonCompleteFunction, SetSectionCompletedResponse, setSectionCompleteFunction } from "@/Interfaces";
import ErrorPage from "../error/ErrorPage";
import { LoaderFunctionArgs, Params, redirect, useLoaderData, useOutletContext, useRouteLoaderData, useSubmit } from "react-router-dom";
import { abortLesson, getSectionData, getSections, setLessonComplete, setSectionComplete as setSectionCompleteReq } from "@/utils/waApiClient";
import { AudioProvider } from "@/components/audioContext/AudioContext";
import { getLocale } from "@/locales/locales";
import { useNavigation } from "@/hooks/useNavigation";
import { logger } from "@/utils/logger";

interface LessonContent {
    [key: number]: JSX.Element;
};
interface Args extends LoaderFunctionArgs {
    params: Params<string>;
}

// Get lesson sections
export const lessonLoader = async ({ params }: Args) => {
    try {
        const { completionId } = params;

        if (!completionId) throw new Error('completionId missing in LessonLoader.');

        const response = await getSections({ completionId });
        return response.data.sections;
    } catch (error) {
        logger.unknownError(error);
        throw error;
    }
};

const Lesson = () => {
    const [canContinue, setCanContinue] = useState<boolean>(false);
    const [sectionsList, setSectionsList] = useState<SectionsList | null>(null);
    const [currentSection, setCurrentSection] = useState<CurrentSection>({});
    const [feedback, setFeedback] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);
    const [callAnswerSaving, setCallAnswerSaving] = useState<boolean>(false);
    const lessonData = useRouteLoaderData('lesson') as LessonData;
    const sections = useLoaderData() as Section[];
    const submit = useSubmit();
    const lang = useOutletContext();
    const completionId = sessionStorage.getItem("COMPLETION_ID") ?? '';
    const { navigation, updateNavigation } = useNavigation({});

    const LESSON_CONTENT: LessonContent = {
        0: <ErrorPage />,
        1: <QuestionForm
            currentSection={currentSection}
            navigation={navigation}
            updateNavigation={updateNavigation}
            setCanContinue={setCanContinue}
            feedback={feedback}
            setFeedback={setFeedback}
            completionId={completionId}
            callAnswerSaving={callAnswerSaving}
            setCallAnswerSaving={setCallAnswerSaving}
        />,
        2: <Media
            currentSection={currentSection}
            navigation={navigation}
            setCanContinue={setCanContinue}
        />,
        // 3: <MediaGroup
        //     currentSection={currentSection}
        //     setCurrentSection={setCurrentSection}
        //     navigation={navigation}
        // />,
        3: <Book
            currentSection={currentSection}
            navigation={navigation}
            setCanContinue={setCanContinue}
            completionId={completionId}
        />,
    };

    useEffect(() => {
        setSectionsList({
            sections: sections,
            sectionsLastIndex: sections.length - 1,
            sectionsFirstIndex: 0
        });
    }, [sections]);

    // Hook for fetching section data by section ID
    useEffect(() => {
        let subscribed: boolean = true;
        const { section } = currentSection;
        if (section) {
            (async () => {
                try {
                    if (!subscribed) return;
                    const url = section.url;
                    const response = await getSectionData({ url });

                    setCurrentSection({
                        ...currentSection,
                        sectionData: response.data.section || response.data.book,
                    });

                } catch (error) {
                    logger.unknownError(error);
                }
            })();

            return () => {
                subscribed = false;
            };
        }
    }, [currentSection.section?.orderingNumber, lang]);

    // Calculate progress
    useEffect(() => {
        const { levelExerciseType } = lessonData;
        switch (levelExerciseType) {
            case 3:
                const { sectionData } = currentSection;
                const { childSectionIndex } = navigation;

                if (childSectionIndex && sectionData?.questionCount) {
                    setProgress(Math.round((childSectionIndex?.current + 1) / sectionData.questionCount * 100));
                }
                break;
            default:
                if (sectionsList?.sections) {
                    const { sections } = sectionsList;

                    let completed = 0;

                    for (let index = 0; index < sections.length; index++) {
                        const element = sections[index];
                        if (element.completedAt) {
                            completed += 1;
                        }
                    }

                    setProgress(Math.round(completed / sections.length * 100));
                }
                break;
        }
    }, [sectionsList?.sections, navigation.childSectionIndex?.current]);

    const setSectionComplete: setSectionCompleteFunction = async () => {
        const { section } = currentSection;

        if (section) {
            const form = new FormData();

            form.append("sectionNumber", section.orderingNumber.toString());
            form.append("completionId", completionId);

            const response: SetSectionCompletedResponse = await setSectionCompleteReq({ form });
            const completedAt = response.data?.completedAt ?? new Date().toISOString();

            section.completedAt = completedAt;
        }
    };

    const setLessonComplete: setLessonCompleteFunction = () => {
        submit(
            {
                lessonId: lessonData.id,
                completionId: completionId,
                intent: "completeLesson"
            },
            {
                method: "post",
                encType: "application/x-www-form-urlencoded",
                replace: true
            }
        );
    };

    const abortLesson = () => {
        submit(
            {
                lessonId: lessonData.id,
                completionId: completionId,
                intent: "abortLesson"
            },
            {
                method: "post",
                encType: "application/x-www-form-urlencoded",
            }
        );
    };

    const saveAnswer = () => {
        setCallAnswerSaving(true);
    }

    return (
        <div id="eLesson" className={styles.mainContainer}>
            <div>
                <div>
                    <div className={styles.elessonFormContainer}>

                        {/* TODO: Last section notification component */}
                        {/* <div id="lastSectionNotification" className="ole-notification-container my-1" hidden>
                            <div className="ole-notification-green d-flex gap-2 align-items-center px-2" role="alert">
                                <div>
                                    <svg className="fs-info" width="24px" height="24px"></svg>
                                </div>
                                <div className="d-flex flex-wrap">
                                    <span className="pe-1"></span>
                                    <b></b>
                                </div>
                            </div>
                        </div> */}

                        <AudioProvider>
                            <div className={styles.contentWrapper}>
                                <div id="form-container">
                                    {currentSection?.section && LESSON_CONTENT[currentSection.section?.type ?? 0]}
                                </div>
                            </div>
                        </AudioProvider>

                        {sectionsList?.sections &&
                            <LessonNavigationBar
                                sectionsList={sectionsList}
                                currentSection={currentSection}
                                setCurrentSection={setCurrentSection}
                                navigation={navigation}
                                updateNavigation={updateNavigation}
                                canContinue={canContinue}
                                setSectionComplete={setSectionComplete}
                                setLessonComplete={setLessonComplete}
                                abortLesson={abortLesson}
                                lessonType={lessonData.levelExerciseType ?? 1}
                                progress={progress}
                                setFeedback={setFeedback}
                                saveAnswer={saveAnswer}
                            />}

                    </div>
                </div>
            </div>
        </div>
    );
};

export const lessonActions = async ({ request }: { request: Request; }) => {
    try {
        const form = await request.formData();
        const intent = form.get("intent");
        const courseId = sessionStorage.getItem('COURSE_ID');
        const drivingLicense = sessionStorage.getItem("DRIVING_LICENSE");
        const courseGroupId = sessionStorage.getItem('COURSE_GROUP_ID');
        const completionId = sessionStorage.getItem('COMPLETION_ID');
        const lang = getLocale();

        if (intent === "completeLesson") {
            const response = await setLessonComplete({ form });
            sessionStorage.setItem('SHOW_CONGRATULATION_MODAL', JSON.stringify({ 'show': response.data.showCongratulationModal, 'type': response.data.lessonLevelExerciseType, 'firstName': response.data.userFirstName }));
            return redirect(`/lesson/${completionId}/completed?lang=${lang}`);
        }

        if (intent === "abortLesson") {
            await abortLesson({ form });
            return redirect(`/portal/${courseId}/${drivingLicense}/group/${courseGroupId}?lang=${lang}`);
        }
    } catch (error) {
        logger.unknownError(error);
        throw error;
    }
};

export default Lesson;